import { createHookContext } from '@clover-util/react-context';
import { useLocalStorageState } from '@clover-util/use-storage-state';
import { useDeliveriesEstimate } from 'hooks/useApi/useDeliveriesEstimateResource';
import { useStorageExpiry } from 'hooks/useStorageExpiry';
import { Address, DeliveryEstimate } from 'olo-service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cartExpiryKey } from './cart/util';
import { formatTimeForDelivery } from './Delivery.util';
import { useMerchant } from './merchant';
import { useScheduledOrderMethods } from './ScheduledOrderMethods';
import { ServiceTypeData } from './serviceType/models';

export enum DeliveriesEstimateErrorType {
  OUT_OF_SERVICE_AREA = 'customer_address_out_of_service_area',
  FUTURE_DATE_REQUIRED = 'future_datetime_required',
}

export const { useDelivery, DeliveryProvider } = createHookContext('Delivery', () => {
  const { merchant, status } = useMerchant();
  const { t } = useTranslation();
  const merchantId = merchant?.merchantUuid;
  const { scheduledTime } = useScheduledOrderMethods();
  const [initialEstimations, setInitialEstimations] = useState<DeliveryEstimate>();
  const [deliveryAddress, setDeliveryAddress] = useLocalStorageState<Address | undefined>(
    merchantId && `colo-delivery_address_${merchantId}`
  );
  const [deliveryMetadata, setDeliveryMetadata] = useLocalStorageState<ServiceTypeData | undefined>(
    merchantId && `colo-delivery_metadata_${merchantId}`
  );

  const [defaultAddressHasBeenLoaded, setDefaultAddressHasBeenLoaded] = useState(false);

  const clearDeliveryData = useCallback(() => {
    setDeliveryAddress(undefined);
    setDeliveryMetadata(undefined);
  }, [setDeliveryAddress, setDeliveryMetadata]);
  const [isExpired] = useStorageExpiry(cartExpiryKey);
  const timezone = merchant?.timezone || 'local';

  // Clear Storage when expired
  useEffect(() => {
    if (isExpired) clearDeliveryData();
  }, [isExpired, clearDeliveryData]);

  const { isDeliveriesEstimateLoading, estimations, error } = useDeliveriesEstimate(
    merchantId,
    deliveryAddress,
    initialEstimations || status !== 'online' ? scheduledTime : undefined
  );

  const deliveryTime = useMemo(() => estimations?.deliveryTime, [estimations?.deliveryTime]);
  const pickupTime = useMemo(() => estimations?.pickupTime, [estimations?.pickupTime]);

  const deliveryAsapTime = useMemo(() => {
    if (!scheduledTime && !initialEstimations) setInitialEstimations(estimations);
    if (!initialEstimations || !estimations) return t('cart.asapDefault');

    const content = `${formatTimeForDelivery(
      initialEstimations?.pickupTime,
      merchant?.leadTime,
      timezone
    )} - ${formatTimeForDelivery(initialEstimations?.deliveryTime, merchant?.leadTime, timezone)}`;

    return t('cart.deliveryAsap', { time: content });
  }, [estimations, initialEstimations, merchant?.leadTime, scheduledTime, t, timezone]);

  return {
    isDeliveriesEstimateLoading,
    deliveryAddress,
    setDeliveryAddress,
    deliveryMetadata,
    setDeliveryMetadata,
    defaultAddressHasBeenLoaded,
    setDefaultAddressHasBeenLoaded,
    deliveryTime,
    pickupTime,
    errorType: useMemo(() => error?.response?.data?.message, [error]),
    clearDeliveryData,
    deliveryAsapTime,
    initialEstimations,
  };
});
